import { useCallback, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { NavActionContext, type NavAction } from "@web/lib/navAction";
import { useQuery } from "@tanstack/react-query";
import { BadgeDollarSign, CalendarCheck, CalendarClock, Inbox, Landmark, MessageSquare, Settings } from "lucide-react";
import { queueCountQuery } from "@web/lib/queries-automation";
import { unreadCountQuery } from "@web/lib/queries-leads";
import { useMe } from "@web/lib/session";
import { AppFrame, NavBar, NavBarAction, TabBar } from "@web/ui";
import { TABS, pageMeta } from "./tabs";

/** Authenticated app chrome: NavBar · screen · TabBar. Screens register the NavBar's trailing action via `useNavAction`. */
export function Shell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const meta = pageMeta(pathname);
  const me = useMe();
  const unread = useQuery(unreadCountQuery).data ?? 0;
  const queued = useQuery(queueCountQuery).data ?? 0;
  // The queue count rides on Today, which is where the queue section lives.
  const tabs = TABS.map(tab => tab.key === "today" && queued > 0 ? { ...tab, badge: queued } : tab);
  const navigation = [...TABS.filter((t) => t.key !== "more"),
    { key: "tours", label: "Tours", path: "/tours", icon: CalendarClock },
    { key: "events", label: "Events", path: "/events", icon: CalendarCheck },
    { key: "queue", label: "Review queue", path: "/more/queue", icon: Inbox, badge: queued },
    { key: "messages", label: "Messages", path: "/more/messages", icon: MessageSquare, badge: unread },
    { key: "accounting", label: "Accounting", path: "/more/accounting", icon: Landmark },
    { key: "commissions", label: "Commissions", path: "/more/commissions", icon: BadgeDollarSign },
    ...(me.companyAdmin ? [{ key: "settings", label: "Settings", path: "/settings", icon: Settings }] : [])];
  const [action, setAction] = useState<NavAction | null>(null);
  const set = useCallback((a: NavAction | null) => setAction(a), []);
  const ctx = useMemo(() => ({ set }), [set]);

  return (
    <NavActionContext.Provider value={ctx}>
      <AppFrame navigation={navigation} pathname={pathname} onNavigate={(path) => navigate({ to: path })} company={me.organization.name} user={me.user.name || me.user.email} role={me.member.role} settingsNavigation={pathname.startsWith("/settings") && me.companyAdmin ? SETTINGS_NAV : undefined}>
      <NavBar
        title={meta.title}
        subtitle={DESCRIPTIONS[meta.title] ?? me.organization.name}
        onBack={meta.back ? () => navigate({ to: meta.back! }) : undefined}
        trailing={action ? <NavBarAction label={action.label} icon={action.icon} onClick={action.onClick} showLabel /> : undefined}
      />
      <Outlet />
      <TabBar items={tabs} active={meta.tab} onSelect={(key) => navigate({ to: TABS.find((t) => t.key === key)!.path })} />
      </AppFrame>
    </NavActionContext.Provider>
  );
}

const DESCRIPTIONS: Record<string, string> = { Today: "A clear view of what needs your attention.", Leads: "Every inquiry. Every next step.", Tours: "A warm welcome starts with a well-planned visit.", Events: "Your upcoming celebrations, all in one place.", Bookings: "Your events, from first plans to final details.", Calendar: "The right space, at the right time.", Accounting: "A clear picture of payments and balances.", Messages: "Keep the conversation moving.", "Review queue": "Nothing goes out until you say so.", Automation: "Decide what happens on its own.", "Message templates": "The words your automation uses.", "Drip sequences": "Long conversations, kept warm.", Commissions: "What is payable, and what has been paid.", Payments: "The money rules.", Settings: "Make the workspace your own." };
const SETTINGS_NAV = [
  { path: "/settings", label: "Overview" }, { path: "/settings/company", label: "Company" },
  { path: "/settings/venues", label: "Venues" }, { path: "/settings/catalog", label: "Catalog" },
  { path: "/settings/event-types", label: "Event types" }, { path: "/settings/lead-sources", label: "Lead sources" },
  { path: "/settings/users", label: "Users" }, { path: "/settings/api-keys", label: "API keys" },
  { path: "/settings/contract-templates", label: "Contract templates" },
  { path: "/settings/message-templates", label: "Message templates" },
  { path: "/settings/drip-sequences", label: "Drip sequences" },
  { path: "/settings/automation", label: "Automation" },
  { path: "/settings/calendar", label: "Calendar" },
  { path: "/settings/payments", label: "Payments" },
];
